import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Award,
  Bell,
  BookOpen,
  BookOpenCheck,
  Building2,
  CalendarDays,
  ChevronDown,
  ClipboardList,
  CreditCard,
  FileText,
  Gift,
  Globe2,
  GraduationCap,
  Headphones,
  Home,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Menu,
  MessageCircle,
  Search,
  ShieldCheck,
  Share2,
  Sparkles,
  Star,
  TicketPercent,
  User,
  UserCheck,
  UserCog,
  UserPlus,
  Users,
  Video,
  X
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { authService } from "../../api/auth.service";
import { branchService } from "../../api/branch.service";
import { courseService } from "../../api/course.service";
import { supportService } from "../../api/support.service";
import { communicationService } from "../../api/communication.service";
import { useLanguage } from "../../contexts/LanguageContext";
// import wiLearnLogo from "../../assets/images/Wilearn.jpeg";
import wiLearnLogo from "../../assets/images/Wi_logo_transparent.png";

const courseImageForMenu = (course) => course.image || course.p_link || course.thumbnail || "";
const courseProviderForMenu = (course, fallback) => course.instructor || course.provider || course.organization || fallback;
const courseDurationForMenu = (course) => course.duration || course.courseDuration || course.course_duration || "";
const courseModeForMenu = (course, fallback) =>
  course.mode || course.learningMode || course.deliveryMode || course.courseMode || (course.liveClassAvailable || course.hasLiveClasses ? "Live" : fallback);
const courseBadgeForMenu = (course) => course.badgeTitle || course.badge || course.courseBadge || "";
const FREE_COURSE_PAID_CERTIFICATE_TYPE = "FREE_COURSE_PAID_CERTIFICATE";
const getCoursePriceForMenu = (course) => Number(course?.price ?? course?.coursePrice ?? course?.amount ?? 0) || 0;
const getCertificatePriceForMenu = (course) => Number(course?.certificatePrice ?? 0) || 0;
const isPaidCertificateCourseForMenu = (course) => course?.courseType === FREE_COURSE_PAID_CERTIFICATE_TYPE;
const isFreeLearningCourseForMenu = (course) =>
  getCoursePriceForMenu(course) <= 0 &&
  (isPaidCertificateCourseForMenu(course) || getCertificatePriceForMenu(course) <= 0);

function Navbar(props) {
  const { t } = useTranslation();
  const { language: currentLanguage, languages, selectedLanguage, changeLanguage } = useLanguage();
  const page = props.page;
  const navigate = useNavigate();
  const location = useLocation();
  const headerRef = useRef(null);
  useLayoutEffect(() => {
    const header = headerRef.current;
    const portal = header?.closest(".student-portal");
    if (!portal) return undefined;
    const measure = () => portal.style.setProperty("--student-header-height", `${header.getBoundingClientRect().height}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, [location.pathname]);
  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileAccountMenuOpen, setIsMobileAccountMenuOpen] = useState(false);
  const [isCourseMenuOpen, setIsCourseMenuOpen] = useState(false);
  const [isFreeLearningMenuOpen, setIsFreeLearningMenuOpen] = useState(false);
  const [mobileExpandedBranches, setMobileExpandedBranches] = useState({});
  const [branches, setBranches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [activeBranch, setActiveBranch] = useState(null);
  const [activeFreeBranch, setActiveFreeBranch] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [communicationUnreadCount, setCommunicationUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isAccountMenuDismissed, setIsAccountMenuDismissed] = useState(false);
  const closeTimerRef = useRef(null);
  const freeLearningCloseTimerRef = useRef(null);
  const courseMenuRef = useRef(null);
  const freeLearningMenuRef = useRef(null);
 
  const [isJoinMenuOpen, setIsJoinMenuOpen] = useState(false);

  const currentUser = authService.getCurrentUser();
  const hasBothPortals = currentUser.roles?.includes("ROLE_USER") && currentUser.roles?.includes("ROLE_INSTRUCTOR");
  const userRole = hasBothPortals && location.pathname.startsWith("/instructor")
    ? "ROLE_INSTRUCTOR"
    : hasBothPortals && location.pathname === "/profile" ? "ROLE_USER" : currentUser?.role;
  const isLearner = userRole === "ROLE_USER" || userRole === "USER";
  const isInstructor = userRole === "ROLE_INSTRUCTOR";
  const isAdmin = userRole === "ROLE_ADMIN";
  const coursesForActiveBranch = activeBranch ? courses.filter((course) => course.branch?.id === activeBranch.id) : [];
  const freeLearningCourses = useMemo(
    () => (courses || []).filter(isFreeLearningCourseForMenu),
    [courses]
  );
  const freeCoursesForActiveBranch = activeFreeBranch
    ? freeLearningCourses.filter((course) => course.branch?.id === activeFreeBranch.id)
    : [];
  const dashboardPath = userRole === "ROLE_ADMIN"
    ? "/admin"
    : userRole === "ROLE_INSTRUCTOR"
      ? "/instructor"
      : "";
  const supportPath = isLearner ? "/profile?tab=tickets" : "/support";

  useEffect(() => {
    const loadBranches = async () => {
      const response = await branchService.getBranches();
      if (response.success) {
        setBranches(response.data || []);
      }
    };
    const loadCourses = async () => {
      const response = await courseService.getAllCourses();
      if (response.success) {
        setCourses(response.data || []);
      }
    };
    loadBranches();
    loadCourses();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setSupportUnreadCount(0);
      setCommunicationUnreadCount(0);
      setNotifications([]);
      return undefined;
    }
    const loadUnread = async () => {
      const [supportResult, communicationResult, notificationResult] = await Promise.all([
        supportService.getUnreadCount(),
        communicationService.getUnreadCount(),
        communicationService.getNotifications()
      ]);
      if (supportResult.success) setSupportUnreadCount(supportResult.data || 0);
      if (communicationResult.success) setCommunicationUnreadCount(communicationResult.data || 0);
      if (notificationResult.success) setNotifications(notificationResult.data || []);
    };
    loadUnread();
    const timer = window.setInterval(loadUnread, 30000);
    return () => window.clearInterval(timer);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!activeBranch && branches.length > 0) {
      setActiveBranch(branches[0]);
    }
    if (!activeFreeBranch && branches.length > 0) {
      setActiveFreeBranch(branches[0]);
    }
  }, [branches, activeBranch, activeFreeBranch]);

  useEffect(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      setSearchResults([]);
      return;
    }
    const filtered = (courses || []).filter((course) => {
      const searchable = `${course.course_name || ""} ${course.branch?.name || ""}`.toLowerCase();
      return searchable.includes(query);
    });
    setSearchResults(filtered.slice(0, 6));
  }, [searchQuery, courses]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (courseMenuRef.current && !courseMenuRef.current.contains(event.target)) {
        closeCourseMenu();
      }
      if (freeLearningMenuRef.current && !freeLearningMenuRef.current.contains(event.target)) {
        closeFreeLearningMenu();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeCourseMenu();
        closeFreeLearningMenu();
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      clearTimeout(closeTimerRef.current);
      clearTimeout(freeLearningCloseTimerRef.current);
    };
  }, []);

  const handleLogOut = async () => {
    await authService.logout();
    setIsAuthenticated(false);
    setIsMobileAccountMenuOpen(false);
    setIsMobileMenuOpen(false);
    navigate("/login");
  };

  const openNotification = async (notification) => {
    await communicationService.markNotificationRead(notification.id);
    setIsNotificationOpen(false);
    navigate(`/communication?conversation=${notification.conversationId}`);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    if (!searchQuery.trim()) return;
    navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    setSearchQuery("");
    setSearchResults([]);
    setIsMobileMenuOpen(false);
  };

  const openCourseMenu = () => {
    clearTimeout(closeTimerRef.current);
    closeFreeLearningMenu();
    setIsCourseMenuOpen(true);
  };

  const scheduleCloseCourseMenu = () => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setIsCourseMenuOpen(false), 180);
  };

  const closeCourseMenu = () => {
    clearTimeout(closeTimerRef.current);
    setIsCourseMenuOpen(false);
  };

  const openFreeLearningMenu = () => {
    clearTimeout(freeLearningCloseTimerRef.current);
    closeCourseMenu();
    setIsFreeLearningMenuOpen(true);
  };

  const scheduleCloseFreeLearningMenu = () => {
    clearTimeout(freeLearningCloseTimerRef.current);
    freeLearningCloseTimerRef.current = setTimeout(() => setIsFreeLearningMenuOpen(false), 180);
  };

  const closeFreeLearningMenu = () => {
    clearTimeout(freeLearningCloseTimerRef.current);
    setIsFreeLearningMenuOpen(false);
  };

  const onLanguageSelect = (languageCode) => {
    changeLanguage(languageCode);
    setIsMobileMenuOpen(false);
  };

  const navLinkClass = (active) =>
  `whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-semibold leading-none transition-all 2xl:px-4 ${active ? "bg-indigo-600 text-white shadow-sm" : "text-slate-700 hover:bg-slate-100"}`;

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setSearchResults([]);
    closeFreeLearningMenu();
  };

  const closeMobileAccountMenu = () => {
    setIsMobileAccountMenuOpen(false);
  };

  const aboutLinks = [
    { label: "About WILEARN", path: "/about" },
    { label: "Global Recognition", path: "/global-recognition" },
    { label: "Validate Certificate", path: "/verify-certificate" },
    { label: "Contact Us", path: "/contact" },
  ];

  const publicQuickLinks = [
    { label: "All Courses", path: "/courses" },
    { label: "Alumni Speak", path: "/alumni-speak" },
    { label: "Corporate Training", path: "/corporate-training" },
    { label: "Internship", path: "/internship" },
    { label: "Careers", path: "/careers" },
  ];

  const joinLinks = [
    { label: "Join as Student", path: "/register" },
    { label: "Join as Teacher", path: "/trainer-register" },
  ];

  const studentNavItems = [
    { label: "Dashboard", path: "/profile" },
    { label: "My Courses", path: "/profile?tab=myCourses" },
    { label: "Live Classes", path: "/profile?tab=liveClasses" },
    { label: "Certificates", path: "/profile?tab=completedCourses" },
    { label: "Rewards", path: "/profile?tab=rewards" },
    { label: "Profile", path: "/profile?tab=profile" },
    { label: "Support", path: supportPath, badge: supportUnreadCount },
    { label: "Communication", path: "/communication", badge: communicationUnreadCount },
    { label: "Refer & Earn", path: "/refer-and-earn" },
  ];

  const instructorNavItems = [
    { label: "Dashboard", path: "/instructor", tab: "dashboard" },
    { label: "My Courses", path: "/instructor?tab=courses", tab: "courses" },
    { label: "Live Classes", path: "/instructor?tab=liveClasses", tab: "liveClasses" },
    { label: "Live Class Requests", path: "/instructor?tab=liveClassRequests", tab: "liveClassRequests" },
    { label: "Monthly Availability", path: "/instructor?tab=availability", tab: "availability" },
    { label: "Champion First", path: "/instructor?tab=championFirst", tab: "championFirst" },
    { label: "Rewards", path: "/instructor?tab=rewards", tab: "rewards" },
    { label: "Support", path: "/instructor?tab=support", tab: "support", badge: supportUnreadCount },
    { label: "Communication", path: "/instructor?tab=communication", tab: "communication", badge: communicationUnreadCount },
    { label: "Refer & Earn", path: "/instructor?tab=refer", tab: "refer" },
    { label: "Profile", path: "/instructor?tab=profile", tab: "profile" },
  ];

  const adminNavItems = [
    { label: "Dashboard", path: "/admin", adminKey: "dashboard" },
    { label: "Users", path: "/admin", adminKey: "user" },
    { label: "Students", path: "/admin", adminKey: "students" },
    { label: "Instructors", path: "/admin", adminKey: "instructors" },
    { label: "Instructor Requests", path: "/admin", adminKey: "instructorRequests" },
    { label: "Branches", path: "/admin", adminKey: "branches" },
    { label: "Courses", path: "/admin", adminKey: "courses" },
    { label: "Certificate Templates", path: "/admin", adminKey: "certificateTemplates" },
    { label: "Live Classes", path: "/admin", adminKey: "liveClasses" },
    { label: "Homepage", path: "/admin", adminKey: "homepage" },
    { label: "Support", path: "/admin", adminKey: "support", badge: supportUnreadCount },
    { label: "Communication", path: "/admin", adminKey: "communication", badge: communicationUnreadCount },
    { label: "Payments", path: "/admin", adminKey: "payments" },
    { label: "Coupons", path: "/admin", adminKey: "coupons" },
    { label: "Rewards", path: "/admin", adminKey: "spinWheel" },
    { label: "Refer & Earn", path: "/admin", adminKey: "referrals" },
  ];

  const accountSections = isAdmin
    ? [{ title: "Admin", items: adminNavItems }]
    : isInstructor
      ? [{ title: "Instructor", items: instructorNavItems }]
      : isLearner
        ? [{ title: "Student", items: studentNavItems }]
        : [];

  const profileImageSrc = useMemo(() => {
    const raw = currentUser?.profileImage || currentUser?.imageUrl || currentUser?.avatarUrl || localStorage.getItem("profileImage") || "";
    if (!raw) return "";
    if (/^(data:|https?:|blob:)/i.test(raw)) return raw;
    return `data:image/jpeg;base64,${raw}`;
  }, [currentUser]);

  const displayRole = isAdmin ? "Admin" : isInstructor ? "Instructor" : isLearner ? "Student" : "Member";
  const displayName = currentUser?.name || currentUser?.username || displayRole;
  const displayEmail = currentUser?.email || t("WiLearn account");
  const initials = String(displayName || "W")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "W";
  const avatarTone = isAdmin
    ? {
        shell: "from-slate-950 via-indigo-800 to-fuchsia-700",
        ring: "ring-fuchsia-200",
        label: "bg-white/15 text-white"
      }
    : isInstructor
      ? {
          shell: "from-cyan-500 via-indigo-600 to-violet-700",
          ring: "ring-cyan-200",
          label: "bg-white/15 text-white"
        }
      : {
          shell: "from-blue-500 via-violet-600 to-pink-500",
          ring: "ring-pink-200",
          label: "bg-white/15 text-white"
        };

  const mobileAccountGroups = useMemo(() => {
    const makeGroup = (title, labels) => ({
      title,
      items: labels
        .map((label) => accountSections.flatMap((section) => section.items).find((item) => item.label === label))
        .filter(Boolean),
    });

    if (isAdmin) {
      return [
        makeGroup("Overview", ["Dashboard", "Homepage", "Payments"]),
        makeGroup("People", ["Users", "Students", "Instructors", "Instructor Requests"]),
        makeGroup("Learning", ["Branches", "Courses", "Certificate Templates", "Live Classes"]),
        makeGroup("Engagement", ["Support", "Communication", "Coupons", "Rewards", "Refer & Earn"]),
      ].filter((group) => group.items.length);
    }
    if (isInstructor) {
      return [
        makeGroup("Teaching", ["Dashboard", "My Courses", "Live Classes", "Live Class Requests", "Monthly Availability"]),
        makeGroup("Growth", ["Champion First", "Rewards", "Refer & Earn"]),
        makeGroup("Account", ["Support", "Communication", "Profile"]),
      ].filter((group) => group.items.length);
    }
    return [
      makeGroup("Learning", ["Dashboard", "My Courses", "Live Classes", "Certificates"]),
      makeGroup("Rewards", ["Rewards", "Refer & Earn"]),
      makeGroup("Account", ["Profile", "Support", "Communication"]),
    ].filter((group) => group.items.length);
  }, [accountSections, isAdmin, isInstructor]);

  const getMobileItemIcon = (item) => {
    const key = item.adminKey || item.tab || item.label;
    const icons = {
      dashboard: LayoutDashboard,
      Dashboard: LayoutDashboard,
      "My Courses": BookOpenCheck,
      courses: BookOpenCheck,
      Courses: BookOpenCheck,
      "Live Classes": Video,
      liveClasses: Video,
      "Live Class Requests": ClipboardList,
      liveClassRequests: ClipboardList,
      "Monthly Availability": CalendarDays,
      availability: CalendarDays,
      "Champion First": Award,
      championFirst: Award,
      Certificates: Award,
      certificateTemplates: FileText,
      "Certificate Templates": FileText,
      Rewards: Gift,
      spinWheel: Gift,
      "Refer & Earn": Share2,
      referrals: Share2,
      Profile: User,
      profile: User,
      Support: Headphones,
      support: Headphones,
      Communication: MessageCircle,
      communication: MessageCircle,
      Users,
      user: Users,
      Students: GraduationCap,
      students: GraduationCap,
      Instructors: UserCheck,
      instructors: UserCheck,
      "Instructor Requests": UserPlus,
      instructorRequests: UserPlus,
      Branches: Building2,
      branches: Building2,
      Homepage: Home,
      homepage: Home,
      Payments: CreditCard,
      payments: CreditCard,
      Coupons: TicketPercent,
      coupons: TicketPercent,
    };
    return icons[key] || Sparkles;
  };

  const isMobileItemActive = (item) => {
    const currentPath = `${location.pathname}${location.search}`;
    if (item.adminKey) {
      const activeKey = localStorage.getItem("adminActiveMenuKey") || "dashboard";
      return location.pathname === "/admin" && activeKey === item.adminKey;
    }
    if (item.tab) {
      const tab = new URLSearchParams(location.search).get("tab") || localStorage.getItem("instructorActiveMenuKey") || "dashboard";
      return location.pathname === "/instructor" && tab === item.tab;
    }
    return currentPath === item.path || location.pathname === item.path;
  };

  const openNavItem = (item) => {
    if (item.adminKey) {
      localStorage.setItem("adminActiveMenuKey", item.adminKey);
      window.dispatchEvent(new CustomEvent("admin-menu-select", { detail: { key: item.adminKey } }));
    }
    if (item.tab) {
      localStorage.setItem("instructorActiveMenuKey", item.tab);
    }
    navigate(item.path);
    closeMobileMenu();
    closeMobileAccountMenu();
    setIsAccountMenuDismissed(true);
    setIsNotificationOpen(false);
  };

  const toggleMobileBranch = (branchId) => {
    setMobileExpandedBranches((current) => ({ ...current, [branchId]: !current[branchId] }));
  };

  const renderCourseMenuCard = (course, { onSelect = closeCourseMenu, showFreeBadges = false, compact = false } = {}) => {
    const image = courseImageForMenu(course);
    const category = course.branch?.name || t("General");
    const provider = courseProviderForMenu(course, category || t("WiLearn"));
    const duration = courseDurationForMenu(course);
    const mode = courseModeForMenu(course, t("Self Paced"));
    const rating = course.averageRating || course.rating || course.avgRating;
    const reviewCount = course.reviewCount || course.reviews || course.totalReviews;
    const badge = courseBadgeForMenu(course);
    const paidCertificate = isPaidCertificateCourseForMenu(course);

    return (
      <Link
        key={course.course_id}
        to={`/courses/${course.course_id}`}
        onClick={onSelect}
        className={`group flex ${compact ? "min-h-[190px]" : "min-h-[210px]"} flex-col overflow-hidden rounded-xl border border-slate-200 bg-white text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-xl`}
      >
        <span className={`${compact ? "h-20" : "h-24"} relative flex w-full flex-shrink-0 items-center justify-center overflow-hidden bg-indigo-50 text-indigo-600`}>
          {image ?
            <img src={image} alt={course.course_name} className="h-full w-full object-cover transition duration-300 group-hover:scale-105" loading="lazy" decoding="async" width="260" height={compact ? "80" : "96"} sizes="(min-width: 1280px) 260px, (min-width: 1024px) 330px, 50vw" /> :
            <BookOpen size={26} />}
          {badge && <span className="absolute right-3 top-3 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-black text-indigo-700 shadow-sm">{t(badge)}</span>}
        </span>
        <span className={`flex min-w-0 flex-1 flex-col ${compact ? "p-3" : "p-4"}`}>
          <span className="line-clamp-2 text-base font-black leading-snug text-slate-950 group-hover:text-indigo-700">{course.course_name}</span>
          <span className="mt-2 block truncate text-xs font-semibold text-slate-500">{category}</span>
          <span className="mt-3 flex flex-wrap gap-2">
            {showFreeBadges && <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-black text-emerald-700">{t("Free")}</span>}
            {showFreeBadges && paidCertificate && <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-black text-amber-700">{t("Paid Certificate")}</span>}
            <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-bold text-indigo-700">{mode}</span>
            {duration && <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">{duration}</span>}
          </span>
          <span className="mt-auto flex min-w-0 items-center justify-between gap-2 pt-4">
            <span className="truncate text-xs font-semibold text-slate-500">{provider}</span>
            <span className="flex shrink-0 items-center gap-1 text-xs font-bold text-amber-700">
              <Star size={13} className="fill-amber-400 text-amber-400" />
              {reviewCount ? `${Number(rating || 0).toFixed(1)} (${Number(reviewCount).toLocaleString("en-IN")})` : t("New")}
            </span>
          </span>
        </span>
      </Link>
    );
  };

  return (

    // <header className="sticky top-0 z-[999] border-b border-slate-200 bg-white/95 backdrop-blur">
     // <header className="sticky top-0 z-[999] border-b border-slate-200 bg-[#F9A8D4]/95 backdrop-blur">
    <header ref={headerRef} className={`${props.portalLayout ? "relative" : "fixed top-0 left-0 right-0"} z-[9999] bg-white/95 backdrop-blur`}>
      {/* <header className="fixed top-0 left-0 right-0 z-[9999] bg-white/95 backdrop-blur shadow-sm border-b border-green-100"> */}
   
      {/* <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 lg:px-6"> */}
      <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8 xl:px-10">
        <Link to="/" className="flex shrink-0 flex-col items-center justify-center gap-0 leading-none">
          {/* <img
            src={wiLearnLogo}
            alt={t("Wi Learn")}
            className="h-12 w-28 rounded-md object-contain"
          /> */}
          <div className="h-10 w-28 overflow-visible flex items-center justify-center">
          <img
            src={wiLearnLogo}
            alt={t("Wi Learn")}
            className="h-12 w-28 object-contain scale-150"
          />
         
        </div>
          {/* <p className="mt-0.5 max-w-36 text-center text-[9px] font-semibold leading-tight text-slate-600 sm:text-[10px]">
            {t("Shape Your Future in the AI-First World")}
          </p> */}
          <p className="mt-2 max-w-none whitespace-nowrap text-center text-[9px] font-semibold leading-tight text-[#fb4b63] sm:text-[10px]">
            {t("Shape Your Future in the AI-First World")}
          </p>
          <div>
            {/* <p className="text-base font-bold tracking-tight text-slate-900">{t("WiLearn")}</p> */}
            {/* <p className="text-3xl font-bold tracking-tight text-slate-900">{t("WiLearn")}</p>
            <p className="text-xs font-medium uppercase tracking-[0.24em] text-slate-500">{t("Learning Platform")}</p> */}
          </div>
        </Link>

        <div className="student-nav-mobile flex items-center gap-2 lg:hidden">
          <button
            type="button"
            className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm"
            onClick={() => {
              if (!isAuthenticated) {
                navigate("/login");
                return;
              }
              setIsMobileMenuOpen(false);
              setSearchResults([]);
              setIsMobileAccountMenuOpen((value) => !value);
            }}
            aria-label={t("Profile menu")}
            aria-expanded={isMobileAccountMenuOpen}
          >
            <User size={19} />
            {(supportUnreadCount + communicationUnreadCount) > 0 && (
              <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-rose-600 ring-2 ring-white" />
            )}
          </button>
          <button
            type="button"
            className="rounded-xl border border-slate-200 bg-white p-2 text-slate-700 shadow-sm"
            onClick={() => {
              setIsMobileAccountMenuOpen(false);
              setIsMobileMenuOpen((value) => !value);
            }}
            aria-label={t("Open menu")}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="student-nav-desktop hidden min-w-0 flex-1 items-center justify-end gap-0.5 lg:flex 2xl:gap-2">
          <div ref={courseMenuRef} className="relative" onMouseEnter={openCourseMenu} onMouseLeave={scheduleCloseCourseMenu}>
            <button type="button" className={navLinkClass(page === "courses")} onClick={() => setIsCourseMenuOpen((value) => !value)} aria-expanded={isCourseMenuOpen}>
              {t("Find Courses")} <ChevronDown size={14} className="inline ml-1" />
            </button>
   
            {isCourseMenuOpen &&
            <div className="fixed left-1/2 top-[68px] z-[1000] w-[min(1180px,calc(100vw-2rem))] -translate-x-1/2 pt-5">
                <div className="h-[min(640px,calc(100vh-108px))] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl ring-1 ring-slate-950/5" onMouseEnter={openCourseMenu}>
                  <div className="grid h-full items-stretch md:grid-cols-[280px_1fr]">
                    <div className="h-full overflow-y-auto border-r border-slate-200 bg-slate-50/90 p-4 scrollbar-thin scrollbar-thumb-slate-300 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300">
                      <p className="px-3 pb-3 text-xs font-black uppercase tracking-wide text-slate-500">{t("Categories")}</p>
                      {branches.length ? branches.map((branch) =>
                    <button key={branch.id} onMouseEnter={() => setActiveBranch(branch)} onClick={() => setActiveBranch(branch)} className={`mb-1 flex w-full items-center justify-between gap-3 rounded-lg px-3 py-3 text-left text-sm font-bold transition ${activeBranch?.id === branch.id ? "bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-100" : "text-slate-700 hover:bg-white hover:text-indigo-700"}`}>
                          <span className="min-w-0 truncate">{branch.name}</span>
                          <ChevronDown size={16} className="flex-shrink-0 rotate-[-90deg]" />
                        </button>
                    ) : <p className="px-3 py-2.5 text-sm text-slate-500">{t("No branches available.")}</p>}
                    </div>
                    <div className="min-h-0 overflow-y-auto bg-white p-6 scrollbar-thin scrollbar-thumb-slate-300 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300">
                      {activeBranch ?
                    <div>
                          <div className="mb-5 flex items-end justify-between gap-4">
                            <div>
                              <p className="text-xs font-black uppercase tracking-wide text-indigo-600">{t("Career Aligned Learning Paths")}</p>
                              <p className="mt-1 text-xl font-black text-slate-950">{activeBranch.name}</p>
                            </div>
                            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
                              {coursesForActiveBranch.length} {t("courses")}
                            </span>
                          </div>
                          <div className="grid gap-5 xl:grid-cols-3 lg:grid-cols-2">
                            {coursesForActiveBranch.map((course) => renderCourseMenuCard(course))}
                            {coursesForActiveBranch.length === 0 && <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">{t("No courses in this branch yet.")}</p>}
                          </div>
                        </div> :

                    <div className="text-sm text-slate-500">{t("Select a branch to preview its courses.")}</div>
                    }
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>

          <div ref={freeLearningMenuRef} className="relative" onMouseEnter={openFreeLearningMenu} onMouseLeave={scheduleCloseFreeLearningMenu}>
            <button type="button" className={navLinkClass(false)} onClick={() => setIsFreeLearningMenuOpen((value) => !value)} aria-expanded={isFreeLearningMenuOpen}>
              {t("Free Learning")} <ChevronDown size={14} className="inline ml-1" />
            </button>

            {isFreeLearningMenuOpen &&
            <div className="fixed left-1/2 top-[68px] z-[1000] w-[min(1180px,calc(100vw-2rem))] -translate-x-1/2 pt-5">
                <div className="h-[min(640px,calc(100vh-108px))] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl ring-1 ring-slate-950/5" onMouseEnter={openFreeLearningMenu}>
                  <div className="grid h-full items-stretch md:grid-cols-[280px_1fr]">
                    <div className="h-full overflow-y-auto border-r border-slate-200 bg-slate-50/90 p-4 scrollbar-thin scrollbar-thumb-slate-300 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300">
                      <p className="px-3 pb-3 text-xs font-black uppercase tracking-wide text-slate-500">{t("Categories")}</p>
                      {branches.length ? branches.map((branch) => {
                        const branchFreeCount = freeLearningCourses.filter((course) => course.branch?.id === branch.id).length;
                        return (
                          <button key={branch.id} onMouseEnter={() => setActiveFreeBranch(branch)} onClick={() => setActiveFreeBranch(branch)} className={`mb-1 flex w-full items-center justify-between gap-3 rounded-lg px-3 py-3 text-left text-sm font-bold transition ${activeFreeBranch?.id === branch.id ? "bg-white text-emerald-700 shadow-sm ring-1 ring-emerald-100" : "text-slate-700 hover:bg-white hover:text-emerald-700"}`}>
                            <span className="min-w-0 truncate">{branch.name}</span>
                            <span className="flex shrink-0 items-center gap-2">
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-black text-slate-500">{branchFreeCount}</span>
                              <ChevronDown size={16} className="flex-shrink-0 rotate-[-90deg]" />
                            </span>
                          </button>
                        );
                      }) : <p className="px-3 py-2.5 text-sm text-slate-500">{t("No branches available.")}</p>}
                    </div>
                    <div className="min-h-0 overflow-y-auto bg-white p-6 scrollbar-thin scrollbar-thumb-slate-300 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300">
                      {activeFreeBranch ? (
                        <div>
                          <div className="mb-5 flex items-end justify-between gap-4">
                            <div>
                              <p className="text-xs font-black uppercase tracking-wide text-emerald-700">{t("Free Learning")}</p>
                              <p className="mt-1 text-xl font-black text-slate-950">{t("Learn for Free")}</p>
                              <p className="mt-1 text-sm font-semibold text-slate-500">{t("Explore free courses and optional certifications.")}</p>
                            </div>
                            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
                              {freeCoursesForActiveBranch.length} {t("courses")}
                            </span>
                          </div>
                          <div className="grid gap-5 xl:grid-cols-3 lg:grid-cols-2">
                            {freeCoursesForActiveBranch.map((course) => renderCourseMenuCard(course, { onSelect: closeFreeLearningMenu, showFreeBadges: true, compact: true }))}
                            {freeCoursesForActiveBranch.length === 0 && <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">{t("No free courses in this branch yet.")}</p>}
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-slate-500">{t("Select a branch to preview its free courses.")}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>

          <div className="group relative">
            <button className={navLinkClass(page === "about")}>{t("About Us")} <ChevronDown size={14} className="inline ml-1" /> </button>
            <div className="invisible absolute left-0 top-full z-[1000] w-48 rounded-xl border border-slate-200 bg-white p-2 pt-3 text-left opacity-0 shadow-[0_18px_45px_rgba(15,23,42,0.16)] ring-1 ring-black/5 transition-all duration-200 ease-out group-hover:visible group-hover:opacity-100">
              {[
              ["About WILEARN", "/about"],
              // ["Global Recognition", "/careers"],
              ["Global Recognition", "/global-recognition"],
              ["Validate Certificate", "/verify-certificate"],
              ["Contact Us", "/contact"]]
              .map(([label, path]) =>
              <Link key={label} to={path} className="block rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-indigo-700">
                  {t(label)}
                </Link>
              )}
            </div>
          </div>

          <form onSubmit={handleSearch} className="relative ml-1 flex min-w-[10rem] max-w-[22rem] flex-1 items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-2 2xl:ml-2">
            <Search size={16} className="shrink-0 text-slate-400" />
            <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={t("Search courses or branches")} className="ml-2 w-full min-w-0 border-0 bg-transparent text-sm outline-none" />
            {searchResults.length > 0 &&
            <div className="absolute left-0 top-full z-[1000] mt-2 max-h-96 w-full min-w-80 overflow-y-auto rounded-2xl border border-slate-200 bg-white py-2 text-left shadow-xl ring-1 ring-black/5">
                {searchResults.map((course) =>
              <button key={course.course_id} type="button" onClick={() => {navigate(`/courses/${course.course_id}`);setSearchQuery("");setSearchResults([]);}} className="block w-full border-b border-slate-100 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none">
                    <span className="block text-sm font-semibold leading-5 text-slate-900">{course.course_name}</span>
                    <span className="mt-1 block text-xs font-medium leading-4 text-slate-500">{course.branch?.name || t("General")}</span>
                  </button>
              )}
              </div>
            }
          </form>

          <div className="group relative ml-1 shrink-0 notranslate 2xl:ml-2" translate="no">
            <button className="flex items-center gap-2 whitespace-nowrap rounded-full border border-slate-200 px-3 py-2 text-sm font-semibold leading-none text-slate-700 notranslate" translate="no">
              <Globe2 size={16} /> {selectedLanguage.label}
            </button>
            <div className="invisible absolute right-0 top-full z-[1000] max-h-[70vh] w-48 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 pt-3 text-left opacity-0 shadow-[0_18px_45px_rgba(15,23,42,0.16)] ring-1 ring-black/5 transition-all duration-200 ease-out group-hover:visible group-hover:opacity-100 notranslate" translate="no">
              {languages.map((language) =>
              <button key={language.code} onClick={() => onLanguageSelect(language.code)} className="flex w-full items-center justify-between rounded-lg px-4 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50 hover:text-indigo-700 notranslate" translate="no">
                  <span>{language.label}</span>
                  {currentLanguage === language.code && <span className="text-indigo-600">✓</span>}
                </button>
              )}
            </div>
          </div>

          {/* <Link to="/register" className="ml-2 rounded-full border border-indigo-200 px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50">{t("Join Us")}</Link> */}
          <div
  className="relative shrink-0"
  onMouseEnter={() => setIsJoinMenuOpen(true)}
  onMouseLeave={() => setIsJoinMenuOpen(false)}
>
  <button
    type="button"
    onClick={() => setIsJoinMenuOpen((prev) => !prev)}
    className="whitespace-nowrap rounded-full border border-indigo-200 px-2.5 py-2 text-sm font-medium leading-none text-indigo-600 transition hover:bg-indigo-50 2xl:px-4"
  >
    {t("Join Us")}
  </button>
  {/* px-5 py-2 */}

  {isJoinMenuOpen && (
    <div className="absolute right-0 top-full pt-2 z-50">
      <div className="w-52 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
        <Link
          to="/register"
          onClick={() => setIsJoinMenuOpen(false)}
          className="block px-4 py-3 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
        >
          {t("Join as Student")}
        </Link>

        <Link
          to="/trainer-register"
          
          onClick={() => setIsJoinMenuOpen(false)}
          className="block px-4 py-3 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
        >
          {t("Join as Teacher")}
        </Link>
      </div>
    </div>
  )}
</div>
          {isAuthenticated ?
          <>
          {/* <div className="relative ml-2">
            <button
              type="button"
              onClick={() => setIsNotificationOpen((value) => !value)}
              className="relative flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50"
              aria-label={t("Notifications")}
            >
              <Bell size={18} />
              {communicationUnreadCount > 0 && <span className="absolute -right-1 -top-1 rounded-full bg-rose-600 px-1.5 py-0.5 text-[10px] font-black text-white">{communicationUnreadCount}</span>}
            </button>
            {isNotificationOpen && (
              <div className="absolute right-0 top-full z-[1000] mt-2 w-96 max-w-[calc(100vw-2rem)] rounded-xl border border-slate-200 bg-white p-2 text-left shadow-2xl">
                <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                  <p className="font-black text-slate-900">{t("Notifications")}</p>
                  <Link to="/communication" onClick={() => setIsNotificationOpen(false)} className="text-xs font-bold text-indigo-600">{t("Open")}</Link>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length === 0 && <p className="px-3 py-4 text-sm text-slate-500">{t("No notifications yet.")}</p>}
                  {notifications.map((notification) => (
                    <button
                      key={notification.id}
                      type="button"
                      onClick={() => openNotification(notification)}
                      className={`w-full rounded-lg px-3 py-3 text-left hover:bg-indigo-50 ${notification.read ? "bg-white" : "bg-indigo-50/70"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-black text-slate-900">{notification.title}</p>
                          <p className="mt-1 line-clamp-2 text-sm text-slate-600">{notification.body}</p>
                          <p className="mt-2 text-xs font-semibold text-slate-400">
                            {notification.sender?.name || "Admin"} · {notification.sender?.role?.replace("ROLE_", "") || "ADMIN"} · {new Date(notification.createdAt).toLocaleString()}
                          </p>
                        </div>
                        {!notification.read && <span className="mt-1 h-2.5 w-2.5 rounded-full bg-rose-600" />}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div> */}
	          <div className="group relative ml-2" onMouseLeave={() => setIsAccountMenuDismissed(false)}>
	              <button className="flex max-w-36 items-center gap-2 rounded-full bg-slate-900 px-2.5 py-2 text-sm font-semibold leading-none text-white 2xl:max-w-56 2xl:px-4">
	                <User size={16} className="shrink-0" /> <span className="truncate whitespace-nowrap">{currentUser.name || t("Profile")}</span>
	              </button>
	              <div className={`invisible absolute right-0 top-full z-[1000] max-h-[70vh] w-72 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 pt-3 text-left opacity-0 shadow-[0_18px_45px_rgba(15,23,42,0.16)] ring-1 ring-black/5 transition-all duration-200 ease-out ${isAccountMenuDismissed ? "" : "group-hover:visible group-hover:opacity-100"}`}>
	                {accountSections.map((section) => (
	                  <div key={section.title} className="mb-2 last:mb-0">
	                    <p className="px-3 pb-1 text-[11px] font-black uppercase tracking-wide text-slate-400">{t(section.title)}</p>
	                    {section.items.map((item) => (
	                      <button
	                        key={`${section.title}-${item.label}`}
	                        type="button"
	                        onClick={() => openNavItem(item)}
	                        className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-indigo-700"
	                      >
	                        <span>{t(item.label)}</span>
	                        {item.badge > 0 && <span className="rounded-full bg-rose-600 px-2 py-0.5 text-xs font-bold text-white">{item.badge}</span>}
	                      </button>
	                    ))}
	                  </div>
	                ))}
	                <button onClick={handleLogOut} className="block w-full rounded-lg px-3 py-2 text-left text-sm font-semibold text-red-600 transition-colors hover:bg-red-50">{t("Sign out")}</button>
	              </div>
	            </div>
            </>
            :
          <Link to="/login" className="ml-1 whitespace-nowrap rounded-full bg-indigo-600 px-3 py-2 text-sm font-semibold leading-none text-white 2xl:ml-2 2xl:px-4">{t("Login")}</Link>
	          }
	        </nav>
	      </div>

			      {isMobileAccountMenuOpen && isAuthenticated &&
			      <div className="fixed inset-x-0 top-[72px] z-[9998] max-h-[calc(100dvh-72px)] overflow-y-auto overflow-x-hidden border-t border-indigo-100 bg-[#f8fbff] shadow-2xl lg:hidden">
			          <div className="mx-auto w-full max-w-md px-4 pb-5 pt-4 text-left">
			            <div className="relative overflow-hidden rounded-[1.35rem] border border-white/70 bg-gradient-to-br from-blue-600 via-violet-600 to-pink-500 p-3 text-white shadow-[0_18px_42px_rgba(79,70,229,0.22)]">
			              <div className="pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full bg-white/18" />
			              <div className="pointer-events-none absolute bottom-0 right-16 h-16 w-24 rounded-t-full bg-white/10" />
			              <div className="relative flex items-start justify-between gap-3">
			                <div className="flex min-w-0 items-center gap-3 text-left">
			                  <div className={`relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br ${avatarTone.shell} text-lg font-black shadow-lg ring-2 ${avatarTone.ring}`}>
			                    {profileImageSrc ? (
			                      <img src={profileImageSrc} alt={displayName} className="h-full w-full object-cover" />
			                    ) : (
			                      <>
			                        <span className="absolute -left-3 -top-3 h-9 w-9 rounded-full bg-white/20" />
			                        <span className="absolute -bottom-4 right-0 h-11 w-11 rounded-full bg-white/15" />
			                        <span className="relative">{initials}</span>
			                      </>
			                    )}
			                  </div>
			                  <div className="min-w-0 text-left">
			                    <p className="text-left text-[11px] font-black uppercase tracking-[0.18em] text-white/80">{t("Portal Menu")}</p>
			                    <p className="mt-1 truncate text-left text-base font-black leading-tight text-white">{displayName}</p>
			                    <div className="mt-1 flex min-w-0 items-center gap-2">
			                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${avatarTone.label}`}>{t(displayRole)}</span>
			                      <span className="truncate text-left text-xs font-semibold text-white/82">{displayEmail}</span>
			                    </div>
			                  </div>
			                </div>
			                <button type="button" onClick={closeMobileAccountMenu} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/95 text-slate-700 shadow-sm" aria-label={t("Close profile menu")}>
			                  <X size={18} />
			                </button>
			              </div>
			            </div>

			            <section className="mt-3 rounded-[1.35rem] border border-indigo-100 bg-white/95 p-3 shadow-[0_14px_36px_rgba(15,23,42,0.08)]">
			              <div className="space-y-3">
			                {mobileAccountGroups.map((group) => (
			                  <div key={group.title}>
			                    <p className="px-1 pb-2 text-left text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">{t(group.title)}</p>
			                    <div className="grid grid-cols-2 gap-2">
			                      {group.items.map((item) => {
			                        const Icon = getMobileItemIcon(item);
			                        const active = isMobileItemActive(item);
			                        return (
			                          <button
			                            key={`${group.title}-${item.label}`}
			                            type="button"
			                            onClick={() => openNavItem(item)}
			                            className={`relative min-h-[3.65rem] rounded-2xl border px-2.5 py-2 text-left transition ${
			                              active
			                                ? "border-violet-200 bg-gradient-to-br from-indigo-50 via-white to-pink-50 text-indigo-800 shadow-sm ring-1 ring-violet-100"
			                                : "border-slate-200 bg-slate-50/70 text-slate-700 hover:border-indigo-200 hover:bg-indigo-50"
			                            }`}
			                          >
			                            <span className="flex items-center gap-2">
			                              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${active ? "bg-gradient-to-br from-indigo-600 to-pink-500 text-white shadow-sm" : "bg-white text-indigo-600 shadow-sm"}`}>
			                                <Icon size={16} />
			                              </span>
			                              <span className="min-w-0 text-left text-[12px] font-black leading-4">{t(item.label)}</span>
			                            </span>
			                            {item.badge > 0 && (
			                              <span className="absolute right-2 top-2 rounded-full bg-rose-600 px-1.5 py-0.5 text-[10px] font-black leading-none text-white shadow-sm">
			                                {item.badge}
			                              </span>
			                            )}
			                          </button>
			                        );
			                      })}
			                    </div>
			                  </div>
			                ))}
			              </div>
			              <div className="mt-3 border-t border-slate-100 pt-3">
			                <button onClick={handleLogOut} className="flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl border border-red-100 bg-red-50 px-4 py-2.5 text-sm font-black text-red-600 transition hover:bg-red-100">
			                  <LogOut size={16} />
			                  {t("Sign out")}
			                </button>
			              </div>
			            </section>
			          </div>
			        </div>
			      }
	
		      {isMobileMenuOpen &&
		      <div className="fixed inset-x-0 top-[72px] z-[9998] max-h-[calc(100dvh-72px)] overflow-y-auto overflow-x-hidden border-t border-slate-200 bg-white shadow-2xl lg:hidden">
	          <div className="mx-auto w-full max-w-md space-y-4 px-4 pb-6 pt-4">
		            <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
		              <div className="min-w-0 text-left">
		                <p className="text-left text-sm font-black text-slate-950">{t("Website Menu")}</p>
		                <p className="mt-0.5 text-left text-xs font-semibold text-slate-500">{t("Courses, branches, public pages and language")}</p>
		              </div>
	              <button type="button" onClick={closeMobileMenu} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-slate-700 shadow-sm" aria-label={t("Close menu")}>
	                <X size={18} />
	              </button>
	            </div>

	            <form onSubmit={handleSearch} className="relative flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-2">
	              <Search size={16} className="text-slate-400" />
	              <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={t("Search courses")} className="ml-2 w-full min-w-0 border-0 bg-transparent text-sm outline-none" />
	              {searchResults.length > 0 &&
	              <div className="absolute left-0 right-0 top-full z-20 mt-2 max-h-64 overflow-y-auto rounded-2xl border border-slate-200 bg-white py-2 text-left shadow-xl ring-1 ring-black/5">
	                  {searchResults.map((course) =>
	                <button key={course.course_id} type="button" onClick={() => {navigate(`/courses/${course.course_id}`);setSearchQuery("");closeMobileMenu();}} className="block w-full border-b border-slate-100 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none">
	                      <span className="block text-sm font-semibold leading-5 text-slate-900">{course.course_name}</span>
	                      <span className="mt-1 block text-xs font-medium leading-4 text-slate-500">{course.branch?.name || t("General")}</span>
	                    </button>
	                )}
	                </div>
	              }
	            </form>

		            <section className="rounded-2xl border border-indigo-100 bg-indigo-50/60 p-3 shadow-sm">
		              <div className="mb-3 flex items-center justify-between gap-3 px-1">
		                <div className="text-left">
		                  <p className="text-left text-xs font-black uppercase tracking-wide text-indigo-600">{t("Find Courses")}</p>
		                  <p className="mt-0.5 text-left text-[11px] font-semibold text-slate-500">{t("Browse by branch or search directly")}</p>
		                </div>
		                <Link to="/courses" onClick={closeMobileMenu} className="text-xs font-black text-indigo-600">{t("All Courses")}</Link>
		              </div>
		              <div className="grid gap-2">
		                {branches.length ? branches.map((branch) => {
	                  const branchCourses = courses.filter((course) => course.branch?.id === branch.id);
	                  const expanded = Boolean(mobileExpandedBranches[branch.id]);
	                  return (
		                    <div key={branch.id} className="rounded-xl border border-indigo-100 bg-white shadow-sm">
		                      <button
	                        type="button"
	                        onClick={() => toggleMobileBranch(branch.id)}
		                        className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-slate-800 hover:bg-indigo-50"
	                        aria-expanded={expanded}
	                      >
	                        <span className="min-w-0 truncate">{branch.name}</span>
	                        <ChevronDown size={16} className={`shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} />
	                      </button>
	                      {expanded && (
		                        <div className="mx-2 mb-2 grid max-h-56 gap-1 overflow-y-auto rounded-xl bg-slate-50 p-2">
		                          {branchCourses.length ? branchCourses.map((course) => (
		                            <Link key={course.course_id} to={`/courses/${course.course_id}`} onClick={closeMobileMenu} className="rounded-lg bg-white px-3 py-2 text-left text-sm font-semibold leading-5 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700">
		                              {course.course_name}
		                            </Link>
	                          )) : <p className="px-3 py-2 text-sm text-slate-500">{t("No courses in this branch yet.")}</p>}
	                        </div>
	                      )}
	                    </div>
	                  );
	                }) : <p className="px-3 py-2 text-sm text-slate-500">{t("No branches available.")}</p>}
		              </div>
		            </section>

		            <section className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-3 shadow-sm">
		              <div className="mb-3 flex items-start justify-between gap-3 px-1">
		                <div className="text-left">
		                  <p className="text-left text-xs font-black uppercase tracking-wide text-emerald-700">{t("Learn for Free")}</p>
		                  <p className="mt-0.5 text-left text-[11px] font-semibold text-slate-500">{t("Explore free courses and optional certifications.")}</p>
		                </div>
		                <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-[11px] font-black text-emerald-700 shadow-sm ring-1 ring-emerald-100">
		                  {freeLearningCourses.length}
		                </span>
		              </div>
		              <div className="grid gap-2">
		                {branches.length ? branches.map((branch) => {
		                  const branchFreeCourses = freeLearningCourses.filter((course) => course.branch?.id === branch.id);
		                  const expanded = Boolean(mobileExpandedBranches[`free-${branch.id}`]);
		                  return (
		                    <div key={`free-${branch.id}`} className="rounded-xl border border-emerald-100 bg-white shadow-sm">
		                      <button
		                        type="button"
		                        onClick={() => toggleMobileBranch(`free-${branch.id}`)}
		                        className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-slate-800 hover:bg-emerald-50"
		                        aria-expanded={expanded}
		                      >
		                        <span className="min-w-0 truncate">{branch.name}</span>
		                        <span className="flex shrink-0 items-center gap-2">
		                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-black text-emerald-700">{branchFreeCourses.length}</span>
		                          <ChevronDown size={16} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
		                        </span>
		                      </button>
		                      {expanded && (
		                        <div className="mx-2 mb-2 grid max-h-[24rem] gap-3 overflow-y-auto rounded-xl bg-slate-50 p-2 pr-1 scrollbar-thin scrollbar-thumb-emerald-200 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-emerald-200">
		                          {branchFreeCourses.length ? branchFreeCourses.map((course) => renderCourseMenuCard(course, { onSelect: closeMobileMenu, showFreeBadges: true, compact: true })) : (
		                            <p className="rounded-xl border border-dashed border-emerald-100 bg-white p-4 text-sm text-slate-500">{t("No free courses in this branch yet.")}</p>
		                          )}
		                        </div>
		                      )}
		                    </div>
		                  );
		                }) : <p className="rounded-xl border border-dashed border-emerald-100 bg-white p-4 text-sm text-slate-500">{t("No branches available.")}</p>}
		              </div>
		            </section>

		            <section className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-3 shadow-sm">
		                <div className="px-1 pb-2 text-left">
		                  <p className="text-left text-xs font-black uppercase tracking-wide text-emerald-700">{t("Join Us")}</p>
		                  <p className="mt-0.5 text-left text-[11px] font-semibold text-slate-500">{t("Create a learner account or apply as a teacher")}</p>
		                </div>
		                <div className="grid gap-2">
		                  {joinLinks.map((item) => (
		                    <Link key={item.path} to={item.path} onClick={closeMobileMenu} className="flex min-h-11 items-center justify-between rounded-xl border border-emerald-100 bg-white px-3 py-2 text-left text-sm font-bold text-emerald-800 hover:bg-emerald-50">
		                      {t(item.label)}
		                      <ChevronDown size={16} className="rotate-[-90deg]" />
		                    </Link>
		                  ))}
		                  {!isAuthenticated && <Link to="/login" onClick={closeMobileMenu} className="flex min-h-11 items-center justify-center rounded-xl bg-indigo-600 px-3 py-2 text-sm font-black text-white">
		                    {t("Login")}
		                  </Link>}
		                </div>
		              </section>

		            <section className="rounded-2xl border border-slate-200 bg-white p-3">
		              <p className="px-1 pb-2 text-left text-xs font-black uppercase tracking-wide text-slate-500">{t("About Us")}</p>
		              <div className="grid gap-1">
		                {aboutLinks.map((item) => (
		                  <Link key={item.path} to={item.path} onClick={closeMobileMenu} className="rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50 hover:text-indigo-700">
	                    {t(item.label)}
	                  </Link>
	                ))}
	              </div>
	            </section>

		            <section className="rounded-2xl border border-slate-200 bg-white p-3">
		              <p className="px-1 pb-2 text-left text-xs font-black uppercase tracking-wide text-slate-500">{t("More")}</p>
		              <div className="grid grid-cols-2 gap-2">
		                {publicQuickLinks.map((item) => (
		                  <Link key={item.path} to={item.path} onClick={closeMobileMenu} className="rounded-xl border border-slate-200 px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50 hover:text-indigo-700">
	                    {t(item.label)}
	                  </Link>
	                ))}
	              </div>
	            </section>

		            <section className="rounded-2xl border border-slate-200 bg-white p-3 notranslate" translate="no">
		              <p className="px-1 pb-2 text-left text-xs font-black uppercase tracking-wide text-slate-500">{t("Language")}</p>
	              <div className="flex max-h-36 flex-wrap gap-2 overflow-y-auto">
	                {languages.map((language) =>
	              <button key={language.code} onClick={() => onLanguageSelect(language.code)} className={`notranslate rounded-full px-4 py-2 text-sm font-semibold ${currentLanguage === language.code ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700"}`} translate="no">
	                    {language.label}
	                  </button>
	              )}
	              </div>
	            </section>
	          </div>
	        </div>
	      }
    </header>);

}

export default Navbar;
